/**
 * Respect VK rate limit max 3 API requests per 1 second
 * 
 * We check times _before_ deciding to allow a call,
 * and save time _after_ a call completes, for tracking,
 * to make sure our time tracking is more strict than that of VK API side.
 * We also add a GAP of some milliseconds for even more relaxed load on VK.
 */

const RL_MAX_NUMBER = 3;
const RL_PERIOD_MS = 1000;
const LOOP_PERIOD_MS = 333;
const TIME_GAP_MS = 100;

// FIFO queue of Tasks
/** @type {Set<Task>} */
const queue = new Set(); // Tasks awaiting execution

/** @type {Set<Task>} */
const stage = new Set(); // Tasks that are in progress

/** Time logging only in DEV */
const RL = "RL"; // label for timer
let timeLogging = false;
function timeLog(...args) {
  if (import.meta?.env?.DEV) {
    if (!timeLogging) {
      timeLogging = true;
      console.time(RL);
    }

    console.timeLog(...args);
  }
}

let countLoops = 0;
let loopTimer;


class Task {
  static counter = 0;

  constructor(fn, resolve, reject) {
    this.fn = fn;
    this.resolve = resolve;
    this.reject = reject;
    this.start = undefined;
    this.end = undefined;

    Task.counter += 1;
    this.id = Task.counter;
    
    timeLog(RL, "+task", this.id);
  }

  /**
   * Run the scheduled task
   * @returns {Promise<void>}
   */
  async run() {
    this.start = Date.now();

    // TODO: maybe update the access_token parameter with the current one.

    return this.fn.call()
      .then(result => {
        this.end = Date.now();
        this.resolve(result);
      })
      .catch(error => this.reject(error));
  }

  /**
   * Check if task has completed far enough in the past
   * @param {number} ts timestamp to compare "end" against
   * @returns {boolean} true if end is in the past
   */
  isCold(ts) {
    if (!this.end) {
      return false;
    }

    return this.end < ts;
  }
}

/**
 * Remove entries that do not count in rate limit anymore.
 * Those, whos "end" time is defined and is enough far in the past.
 */
function refreshStage() {
  const now = Date.now();

  let unknown = 0;
  let done = 0;

  // eslint-disable-next-line unicorn/no-array-for-each
  stage.forEach(task => {
    if (task.end) {
      done += 1;
    } else {
      unknown += 1;
    }

    if (task.isCold(now - (RL_PERIOD_MS + TIME_GAP_MS))) {
      stage.delete(task);
    }
  });

  return [unknown, done];
}

/**
 * Schedule checkAndRun call after some time
 * @param {number} ms to timeout
 */
function reloop(ms = LOOP_PERIOD_MS) {
  if (loopTimer) {
    clearTimeout(loopTimer);
  }

  loopTimer = setTimeout(checkAndRun, ms);
}

/**
 * Event loop iteration, sort of.
 * We run it by plain setTimeout().
 * Chain is stopped when no tasks are queued, relaunched upon new incoming task.
 */
async function checkAndRun() {
  countLoops += 1;
  clearTimeout(loopTimer);
  loopTimer = undefined;

  const [u, d] = refreshStage();
  const isBusy = stage.size >= RL_MAX_NUMBER;
  const isNoTasks = queue.size === 0;
  timeLog(RL, `check ${isBusy ? 'BUSY' : ''} ${isNoTasks ? 'SLEEP' : ''}`, u, d);

  if (isBusy) {
    reloop(LOOP_PERIOD_MS);

    return;
  }

  if (isNoTasks) {
    return;
  }

  // Extract from queue and put to stage
  const task = queue.values().next().value;
  queue.delete(task);
  stage.add(task);

  timeLog(RL, "  run task", task.id);
  task.run()
    .then(() => {
      timeLog(RL, `    task ${task.id} completed. Loops: ${countLoops}, queue: ${queue.size}`);

      if (queue.length > 0) {
        reloop(0);
      }
    })
    .catch(error => console.error(error))
    ;

  reloop(0);
}

/**
 * Wrap async function into Promise and resolve as soon as possible
 * with respect to the rate limit.
 * 
 * @param {CallableFunction} fn 
 * @returns 
 */
export default async function rateLimit(fn) {
  return new Promise((res, rej) => {
    queue.add(new Task(fn, res, rej));

    if (!loopTimer) {
      timeLog(RL, "WAKE UP");
      reloop(0);
    }
  });
}
